import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { bidService } from '../services/bidService';
import {
  uploadFile,
  generateAttachmentFolderId,
  extractFolderIdFromAttachmentUrl,
  isPublicAttachmentUrl,
} from '../services/attachmentService';
import { setError, setLoading } from '../store/slices/bidsSlice';
import {
  BID_CATEGORY_DROPDOWN_OPTIONS,
  BID_DURATIONS,
  CATEGORIES,
  CATEGORY_CUSTOM_OPTION,
  CATEGORY_PLACEHOLDER_OPTION,
  CURRENCIES,
  DECREMENTAL_OPTIONS,
  DEFAULT_CATEGORY,
} from '../constants';
import type { Bid, BidAttachment } from '../types';
import { FieldError, inputErrorClass } from '../components/FieldError';
import { BackArrowIcon } from '../components/BackArrowIcon';

const MAX_ATTACHMENTS = 2;
const MAX_TOTAL_BYTES = 2 * 1024 * 1024;
const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];

type LocalAtt = { name: string; file?: File; url?: string; sizeBytes: number };

function getInitialDates() {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startTime = new Date(now.getTime() + 30 * 60 * 1000);
  startTime.setSeconds(0, 0);
  return { startDate, startTime };
}

/** `<input type="date">` value must be local calendar YYYY-MM-DD — never use `toISOString()` (UTC shifts the day). */
function formatDateForInput(d: Date): string {
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateInputValue(value: string): Date {
  const [y, mo, da] = value.split('-').map((x) => parseInt(x, 10));
  if (!y || !mo || !da) return new Date(NaN);
  return new Date(y, mo - 1, da);
}

export function CreateBidPage() {
  const { editBidId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { startDate: initD, startTime: initT } = getInitialDates();

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [customCategory, setCustomCategory] = useState('');
  const [currency, setCurrency] = useState(CURRENCIES[0].code);
  const [ceilingPrice, setCeilingPrice] = useState('');
  const [decrementalPercent, setDecrementalPercent] = useState(DECREMENTAL_OPTIONS[0]);
  const [durationMinutes, setDurationMinutes] = useState(BID_DURATIONS[0]);
  /** Not shown in UI (aligned with mobile); still sent to API — default 1, preserved when editing. */
  const itemQuantityRef = useRef(1);
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState(initD);
  const [startTime, setStartTime] = useState(initT);
  const [invitedEmails, setInvitedEmails] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [rankOnlyMode, setRankOnlyMode] = useState(false);
  const [attachments, setAttachments] = useState<LocalAtt[]>([]);
  const [folderId, setFolderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Prevents overlapping submits (double-click) while async work runs. */
  const submitLockRef = useRef(false);

  const loadBid = useCallback(async () => {
    if (!editBidId) {
      const { startDate: d, startTime: t } = getInitialDates();
      setTitle('');
      setCategory(DEFAULT_CATEGORY);
      setCustomCategory('');
      setCurrency(CURRENCIES[0].code);
      setCeilingPrice('');
      setDecrementalPercent(DECREMENTAL_OPTIONS[0]);
      setDurationMinutes(BID_DURATIONS[0]);
      itemQuantityRef.current = 1;
      setDescription('');
      setStartDate(d);
      setStartTime(t);
      setInvitedEmails('');
      setCcEmail('');
      setRankOnlyMode(false);
      setAttachments([]);
      setFolderId('');
      return;
    }
    try {
      const bid: Bid = await bidService.getBid(editBidId);
      const startDt = new Date(bid.startTime);
      const isCustom = !CATEGORIES.includes(bid.item.category);
      const incoming: BidAttachment[] =
        Array.isArray(bid.attachments) && bid.attachments.length > 0
          ? bid.attachments
          : bid.attachmentUrl
            ? [{ fileName: bid.attachmentName || 'Attachment', fileUrl: bid.attachmentUrl }]
            : [];
      const valid = incoming
        .filter((a) => isPublicAttachmentUrl(a.fileUrl))
        .slice(0, MAX_ATTACHMENTS)
        .map((a) => ({
          name: a.fileName,
          url: a.fileUrl,
          sizeBytes: typeof a.fileSizeBytes === 'number' ? a.fileSizeBytes : 0,
        }));
      const fid = valid.length > 0 ? extractFolderIdFromAttachmentUrl(valid[0].url || '') || '' : '';
      setTitle(bid.title);
      setCategory(isCustom ? CATEGORY_CUSTOM_OPTION : bid.item.category);
      setCustomCategory(isCustom ? bid.item.category : '');
      setCurrency(bid.item.currency);
      setCeilingPrice(String(bid.item.initialPrice));
      setDecrementalPercent(bid.decrementalPercentage);
      setDurationMinutes(bid.durationMinutes ?? BID_DURATIONS[0]);
      itemQuantityRef.current = Number(bid.item.quantity) > 0 ? Number(bid.item.quantity) : 1;
      setDescription(bid.item.description ?? '');
      setStartDate(new Date(startDt.getFullYear(), startDt.getMonth(), startDt.getDate()));
      setStartTime(new Date(startDt));
      setInvitedEmails((bid.invitedEmails ?? []).join(', '));
      setCcEmail(bid.ccEmail ?? '');
      setRankOnlyMode(Boolean(bid.rankOnlyMode));
      setAttachments(valid);
      setFolderId(fid);
    } catch {
      navigate('/home');
    }
  }, [editBidId, navigate]);

  useEffect(() => {
    loadBid();
    setErrors({});
  }, [loadBid]);

  const activeCurrency = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
  const ceilingNum = Number(ceilingPrice) || 0;
  const decrementalValue = (ceilingNum * decrementalPercent) / 100;

  const parseEmails = () =>
    invitedEmails
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.includes('@'));

  const totalAttSize = () => attachments.reduce((s, a) => s + (a.sizeBytes || 0), 0);

  const onPickFiles = (files: FileList | null) => {
    if (!files?.length) return;
    if (attachments.length >= MAX_ATTACHMENTS) {
      window.alert(`Maximum ${MAX_ATTACHMENTS} documents.`);
      return;
    }
    const file = files[0];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      window.alert('Only PDF, DOC, DOCX, XLS, XLSX allowed.');
      return;
    }
    const nextTotal = totalAttSize() + file.size;
    if (nextTotal > MAX_TOTAL_BYTES) {
      window.alert('Total attachment size cannot exceed 2 MB.');
      return;
    }
    const fid = folderId || generateAttachmentFolderId();
    setFolderId(fid);
    setAttachments((prev) => [...prev, { name: file.name, file, sizeBytes: file.size }]);
  };

  const removeAtt = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    setErrors({});
    const emails = parseEmails();
    let finalCat = '';
    if (category === CATEGORY_PLACEHOLDER_OPTION) {
      finalCat = '';
    } else if (category === CATEGORY_CUSTOM_OPTION) {
      finalCat = customCategory.trim();
    } else {
      finalCat = category;
    }
    const e: Record<string, string> = {};
    if (!title.trim()) e.title = 'Bid title is required.';
    if (category === CATEGORY_PLACEHOLDER_OPTION) {
      e.category = 'Please select a category.';
    } else if (!finalCat) {
      e.category = 'Category is required.';
    }
    if (!ceilingPrice || ceilingNum <= 0) e.ceilingPrice = 'Valid ceiling price required.';
    if (emails.length < 2) e.invitedEmails = 'At least 2 vendor emails required.';
    if (emails.length > 10) e.invitedEmails = 'Maximum 10 emails.';

    let combined: Date | null = null;
    if (Number.isNaN(startDate.getTime())) {
      e.startDateTime = 'Invalid start date.';
    } else if (Number.isNaN(startTime.getTime())) {
      e.startDateTime = 'Invalid start time.';
    } else {
      combined = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        startTime.getHours(),
        startTime.getMinutes(),
        0,
        0
      );
      if (Number.isNaN(combined.getTime())) {
        e.startDateTime = 'Invalid start date or time.';
      } else if (combined.getTime() <= Date.now()) {
        e.startDateTime = 'Start must be in the future.';
      }
    }

    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    if (!combined) {
      setErrors({ startDateTime: 'Invalid start date or time.' });
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setBusy(true);
    dispatch(setLoading(true));
    try {
      const fid = folderId || generateAttachmentFolderId();
      const uploaded: BidAttachment[] = [];
      for (const att of attachments) {
        if (att.url && isPublicAttachmentUrl(att.url)) {
          uploaded.push({ fileName: att.name, fileUrl: att.url, fileSizeBytes: att.sizeBytes });
          continue;
        }
        if (att.file) {
          const res = await uploadFile(att.file, fid);
          uploaded.push({
            fileName: res.fileName,
            fileUrl: res.url,
            fileSizeBytes: att.sizeBytes,
          });
        }
      }

      const startIso = combined.toISOString();
      const payload = {
        title,
        item: {
          name: title,
          description,
          initialPrice: ceilingNum,
          quantity: itemQuantityRef.current,
          category: finalCat,
          currency,
        },
        startTime: startIso,
        durationMinutes,
        decrementalPercentage: decrementalPercent,
        rankOnlyMode,
        attachments: uploaded,
        invitedEmails: emails,
        ccEmail: ccEmail || undefined,
      };

      if (editBidId) {
        await bidService.updateBid(editBidId, payload);
        await queryClient.invalidateQueries({ queryKey: ['bids'] });
        window.alert('Bid updated.');
        navigate('/home');
      } else {
        await bidService.createBid(payload);
        await queryClient.invalidateQueries({ queryKey: ['bids'] });
        window.alert('Bid created.');
        navigate('/home');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed';
      const uploadUserMessage = 'Problem uploading the document, please try after some time';
      if (msg === 'Invalid time value' || msg.includes('Invalid time')) {
        const hint =
          'Invalid start date or time. Please re-select the start date and start time (use 24-hour clock).';
        setErrors({ startDateTime: hint });
        dispatch(setError(hint));
        window.alert(hint);
      } else if (
        msg === uploadUserMessage ||
        /uniform bucket-level access|Cannot update access control|storage\.googleapis\.com/i.test(msg)
      ) {
        setErrors({ submit: uploadUserMessage });
        dispatch(setError(uploadUserMessage));
        window.alert(uploadUserMessage);
      } else {
        setErrors({ submit: msg });
        dispatch(setError(msg));
        window.alert(msg);
      }
    } finally {
      submitLockRef.current = false;
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  const emailCount = parseEmails().length;

  return (
    <div className="pb-24">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/home" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500">
          <BackArrowIcon />
          Back
        </Link>
        <h1 className="text-lg font-black text-blue-600">{editBidId ? 'Edit Bid' : 'Create Bid'}</h1>
      </div>

      <label className="text-[10px] font-black text-blue-600 uppercase">Bid title</label>
      <input
        className={`w-full rounded-2xl border border-slate-200 px-4 py-3 mb-1 placeholder:text-slate-400 ${errors.title ? inputErrorClass : ''}`}
        placeholder="e.g. Office Chairs Q3"
        value={title}
        onChange={(ev) => setTitle(ev.target.value)}
      />
      <FieldError message={errors.title} />

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Category</label>
          <select
            className={`w-full rounded-2xl border border-slate-200 px-3 py-3 mt-1 ${errors.category && category === CATEGORY_PLACEHOLDER_OPTION ? inputErrorClass : ''}`}
            value={category}
            onChange={(ev) => {
              setCategory(ev.target.value);
              if (errors.category) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.category;
                  return next;
                });
              }
            }}
          >
            {BID_CATEGORY_DROPDOWN_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {category !== CATEGORY_CUSTOM_OPTION ? (
            <FieldError message={errors.category} />
          ) : null}
        </div>
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Duration (min)</label>
          <select
            className="w-full rounded-2xl border border-slate-200 px-3 py-3 mt-1"
            value={durationMinutes}
            onChange={(ev) => setDurationMinutes(Number(ev.target.value))}
          >
            {BID_DURATIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {category === CATEGORY_CUSTOM_OPTION && (
        <div className="mt-4">
          <label className="text-[10px] font-black text-blue-600 uppercase">Custom category</label>
          <input
            className={`w-full rounded-2xl border border-slate-200 px-4 py-3 mt-1 placeholder:text-slate-400 ${errors.category ? inputErrorClass : ''}`}
            placeholder="Type category..."
            value={customCategory}
            onChange={(ev) => setCustomCategory(ev.target.value)}
          />
          <FieldError message={errors.category} />
        </div>
      )}

      <div className="mt-4">
        <label className="mb-1 block text-[10px] font-black uppercase text-blue-600">
          Description &amp; Attachment
        </label>
        <textarea
          className="mt-1 min-h-[100px] w-full rounded-2xl border border-slate-200 px-4 py-3 placeholder:text-slate-400"
          placeholder="Enter procurement details..."
          value={description}
          onChange={(ev) => setDescription(ev.target.value)}
        />
        <div className="mt-2">
          {attachments.map((a, i) => (
            <div
              key={i}
              className="mb-2 flex min-w-0 items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-100 p-3 text-sm"
            >
              <span className="min-w-0 flex-1 truncate text-blue-900">
                📄 {a.name}
              </span>
              <button
                type="button"
                className="shrink-0 text-lg leading-none"
                aria-label="Remove attachment"
                onClick={() => removeAtt(i)}
              >
                🗑️
              </button>
            </div>
          ))}
          {attachments.length < MAX_ATTACHMENTS && (
            <label className="mt-2 inline-block cursor-pointer rounded-xl border border-blue-200 bg-blue-100 px-3 py-3 text-xs font-bold text-blue-600">
              📎 Attach Document ({attachments.length}/{MAX_ATTACHMENTS})
              <input
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={(ev) => onPickFiles(ev.target.files)}
              />
            </label>
          )}
          <p className="mt-2 text-[9px] italic text-slate-400">
            Allowed: XLS, XLSX, DOC, DOCX, PDF. Total max size: 2 MB.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Currency</label>
          <select
            className="w-full rounded-2xl border border-slate-200 px-3 py-3 mt-1"
            value={currency}
            onChange={(ev) => setCurrency(ev.target.value)}
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Ceiling price</label>
          <div className={`flex items-center rounded-2xl border border-slate-200 mt-1 ${errors.ceilingPrice ? inputErrorClass : ''}`}>
            <span className="pl-3 text-blue-600 font-bold text-sm">{activeCurrency.symbol}</span>
            <input
              className="flex-1 px-2 py-3 rounded-2xl outline-none placeholder:text-slate-400"
              inputMode="decimal"
              placeholder="0.00"
              value={ceilingPrice}
              onChange={(ev) => setCeilingPrice(ev.target.value)}
            />
          </div>
          <FieldError message={errors.ceilingPrice} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 bg-blue-50 border border-blue-100 rounded-2xl p-4">
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Decrement %</label>
          <select
            className="w-full rounded-xl border border-blue-200 px-2 py-2 mt-1 bg-white"
            value={decrementalPercent}
            onChange={(ev) => setDecrementalPercent(Number(ev.target.value))}
          >
            {DECREMENTAL_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}%
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Decrement value</label>
          <div className="mt-1 px-3 py-2 bg-slate-100 rounded-xl font-black text-blue-700 text-sm">
            {activeCurrency.symbol}
            {decrementalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Start date</label>
          <input
            type="date"
            className={`w-full rounded-2xl border border-slate-200 px-3 py-3 mt-1 ${errors.startDateTime ? inputErrorClass : ''}`}
            value={formatDateForInput(startDate)}
            onChange={(ev) => setStartDate(parseDateInputValue(ev.target.value))}
          />
        </div>
        <div>
          <label className="text-[10px] font-black text-blue-600 uppercase">Start time</label>
          <input
            type="time"
            className={`w-full rounded-2xl border border-slate-200 px-3 py-3 mt-1 ${errors.startDateTime ? inputErrorClass : ''}`}
            value={`${String(startTime.getHours()).padStart(2, '0')}:${String(startTime.getMinutes()).padStart(2, '0')}`}
            onChange={(ev) => {
              const v = ev.target.value;
              if (!v) return;
              const parts = v.split(':');
              if (parts.length < 2) return;
              const h = parseInt(parts[0], 10);
              const m = parseInt(parts[1], 10);
              if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return;
              const base = Number.isNaN(startTime.getTime()) ? new Date(startDate) : new Date(startTime);
              base.setHours(h, m, 0, 0);
              setStartTime(base);
            }}
          />
        </div>
      </div>
      <FieldError message={errors.startDateTime} />

      {/* Matches mobile CreateBidScreen: toggleRow + Switch track/thumb colors */}
      <div className="mt-6 mb-4 flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-0 flex-1 pr-2">
          <p className="mb-1 text-[10px] font-black uppercase leading-snug text-blue-600">
            Rank only mode (Hide prices for bidders)
          </p>
          <p className="text-[9px] italic leading-relaxed text-slate-400">
            When ON, participants see rank but winning price stays hidden.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={rankOnlyMode}
          aria-label="Rank only mode"
          onClick={() => setRankOnlyMode((v) => !v)}
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${rankOnlyMode ? 'bg-blue-300' : 'bg-[#cbd5e1]'}`}
        >
          <span
            className={`absolute top-1 h-5 w-5 rounded-full shadow-sm transition-all duration-200 ease-out ${
              rankOnlyMode ? 'left-6 bg-blue-600' : 'left-1 bg-[#f8fafc]'
            }`}
          />
        </button>
      </div>

      <div className="mt-6">
        <div className="flex justify-between items-center mb-1">
          <label className="text-[10px] font-black text-blue-600 uppercase">Invite vendors (email IDs)</label>
          <span
            className={`text-[10px] font-black uppercase ${emailCount >= 2 && emailCount <= 10 ? 'text-emerald-600' : 'text-amber-600'}`}
          >
            {emailCount} / 10 invited
          </span>
        </div>
        <textarea
          className={`w-full rounded-2xl border border-slate-200 px-4 py-3 min-h-[72px] placeholder:text-slate-400 ${errors.invitedEmails ? inputErrorClass : ''}`}
          placeholder="Separate emails with commas (Min 2 required)"
          value={invitedEmails}
          onChange={(ev) => setInvitedEmails(ev.target.value)}
        />
        <p className="text-[10px] text-slate-400 mt-1.5 italic">Example: vendor1@mail.com, vendor2@mail.com</p>
        <FieldError message={errors.invitedEmails} />
      </div>

      <div className="mt-4">
        <label className="text-[10px] font-black text-blue-600 uppercase">CC notification email (optional)</label>
        <input
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 mt-1 placeholder:text-slate-400"
          placeholder="additional-receipt@company.com"
          value={ccEmail}
          onChange={(ev) => setCcEmail(ev.target.value)}
        />
        <p className="text-[10px] text-slate-400 mt-1.5 italic">
          Bid results will be sent to this email upon conclusion.
        </p>
      </div>

      <FieldError message={errors.submit} />
      <button
        type="button"
        disabled={busy}
        onClick={submit}
        className="w-full py-4 mt-8 rounded-2xl bg-blue-600 text-white font-black uppercase disabled:opacity-50"
      >
        {busy ? '...' : editBidId ? 'Update bid' : 'Publish bid'}
      </button>
    </div>
  );
}
